/**
 * errorReporter.js - Telemetria de errores de JavaScript del frontend.
 *
 * Reusa POST /api/track (que ya acepta metadata libre y la guarda en
 * activity_events.metadata), asi que no necesita nada nuevo en el backend.
 * Fire-and-forget: nunca bloquea ni relanza.
 *
 * Para consultar los errores de un cliente:
 *   SELECT created_at, username, module, metadata
 *   FROM activity_events WHERE action = 'js_error' ORDER BY created_at DESC;
 */

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Tope por sesion: un error en render puede dispararse en bucle y no queremos
// inundar activity_events ni la red del usuario.
const MAX_REPORTS_PER_SESSION = 10;
let reportCount = 0;
let lastSignature = '';

/**
 * Envia un error de JS al backend.
 *
 * @param {string} username - Usuario de Cognito (si no hay, no se reporta)
 * @param {string} module - Modulo donde ocurrio (floating_chat, app, reports...)
 * @param {Error|string} error - Error capturado
 * @param {object} extra - Datos adicionales (componentStack, source, etc.)
 */
export const reportClientError = (username, module, error, extra = {}) => {
    if (!username) return;
    if (reportCount >= MAX_REPORTS_PER_SESSION) return;

    const message = (error && error.message) || String(error || 'unknown');
    const stack = (error && error.stack) || '';

    // Deduplica el mismo error repetido (React reintenta el render antes de
    // desmontar, asi que el mismo stack suele llegar 2-3 veces seguidas).
    const signature = `${module}|${message}|${stack.slice(0, 200)}`;
    if (signature === lastSignature) return;
    lastSignature = signature;
    reportCount += 1;

    try {
        fetch(`${STRANDS_API_URL}/api/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                username,
                module,
                action: 'js_error',
                metadata: {
                    message: message.slice(0, 500),
                    stack: stack.slice(0, 3000),
                    url: window.location.pathname,
                    user_agent: navigator.userAgent.slice(0, 200),
                    ...extra,
                },
            }),
        }).catch(() => {});
    } catch (e) { /* nunca romper por telemetria */ }
};

/**
 * Engancha window.onerror y unhandledrejection. Idempotente.
 * Devuelve la funcion de limpieza.
 */
export const installGlobalErrorReporter = (username) => {
    if (!username) return () => {};

    const onError = (event) => {
        reportClientError(username, 'app', event.error || event.message, {
            source: 'window.onerror',
            file: `${event.filename || ''}:${event.lineno || ''}`,
        });
    };

    const onRejection = (event) => {
        reportClientError(username, 'app', event.reason, {
            source: 'unhandledrejection',
        });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onRejection);
    };
};

export default reportClientError;
